import React, { useState, useEffect, useRef } from "react";
import { useWalletKit } from "@mysten/wallet-kit";
import { TransactionBlock } from "@mysten/sui.js/transactions";
import "./EnhancedCreateTokenForm.css";

import EnhancedTokenPreview from "./EnhancedTokenPreview";

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL || "http://localhost:4000/api";

interface CreateTokenFormProps {
  onCreated: () => void;
}

const EnhancedCreateTokenForm: React.FC<CreateTokenFormProps> = ({
  onCreated,
}) => {
  const { signAndExecuteTransactionBlock, currentAccount, isConnected } =
    useWalletKit();
  const [form, setForm] = useState({
    name: "",
    symbol: "",
    description: "",
    imageUrl: "",
    decimals: 9,
    maxSupply: "100000000000000",
    feeBps: 300,
  });
  const [file, setFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [publishDigest, setPublishDigest] = useState<string | null>(null);
  const [bondingDigest, setBondingDigest] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [bondingCurveProgress, setBondingCurveProgress] = useState(0);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Inject CSS to force dark theme on inputs
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .form-input, .form-textarea {
        background-color: #1e1e1e !important;
        color: #ffffff !important;
        border: 1px solid #333333 !important;
      }
      .form-input:focus, .form-textarea:focus {
        background-color: #1e1e1e !important;
        color: #ffffff !important;
        border: 1px solid #10b981 !important;
      }
      .form-input::placeholder, .form-textarea::placeholder {
        color: #888888 !important;
      }
      .form-input:-webkit-autofill,
      .form-input:-webkit-autofill:hover,
      .form-input:-webkit-autofill:focus,
      .form-input:-webkit-autofill:active {
        -webkit-box-shadow: 0 0 0 30px #1e1e1e inset !important;
        -webkit-text-fill-color: #ffffff !important;
        background-color: #1e1e1e !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const waitForUrl = async (url: string) => {
    const maxTries = 15;
    const delay = (ms: number) => new Promise((r) => setTimeout(r, ms));
    for (let i = 0; i < maxTries; i++) {
      try {
        const res = await fetch(url, { method: "HEAD", mode: "cors" });
        if (res.ok) return true;
      } catch { }
      await delay(1000);
    }
    return false;
  };

  // Animate bonding curve based on form completion
  useEffect(() => {
    const completionFields = [
      form.name,
      form.symbol,
      form.description,
      form.imageUrl || previewImage,
    ];
    const completion = completionFields.filter(
      (field) => field && field.trim()
    ).length;
    const targetProgress = (completion / completionFields.length) * 100;

    // Use form completion for the preview, but real data will be fetched once token is created
    setBondingCurveProgress(targetProgress);
  }, [form, previewImage]);

  // Draw bonding curve animation
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const width = canvas.width;
    const height = canvas.height;

    // Clear canvas
    ctx.clearRect(0, 0, width, height);

    // Create gradient
    const gradient = ctx.createLinearGradient(0, height, 0, 0);
    gradient.addColorStop(0, "rgba(16, 185, 129, 0.8)");
    gradient.addColorStop(1, "rgba(52, 211, 153, 0.4)");

    // Draw curve
    ctx.beginPath();
    ctx.strokeStyle = "#10b981";
    ctx.lineWidth = 3;
    ctx.lineCap = "round";

    const points = [];
    for (let i = 0; i <= width; i += 10) {
      const progress = ((i / width) * bondingCurveProgress) / 100;
      const x = i;
      const y =
        height - progress * Math.pow(i / width, 1.5) * height * 0.8 - 20;
      points.push({ x, y });
    }

    points.forEach((point, index) => {
      if (index === 0) {
        ctx.moveTo(point.x, point.y);
      } else {
        ctx.lineTo(point.x, point.y);
      }
    });
    ctx.stroke();

    // Fill area under curve
    ctx.beginPath();
    ctx.fillStyle = gradient;
    points.forEach((point, index) => {
      if (index === 0) {
        ctx.moveTo(point.x, point.y);
      } else {
        ctx.lineTo(point.x, point.y);
      }
    });
    ctx.lineTo(width, height);
    ctx.lineTo(0, height);
    ctx.closePath();
    ctx.fill();

    // Draw animated dot at progress point
    if (bondingCurveProgress > 0) {
      const dotProgress = bondingCurveProgress / 100;
      const dotX = width * dotProgress;
      const dotY =
        height - dotProgress * Math.pow(dotProgress, 1.5) * height * 0.8 - 20;

      ctx.beginPath();
      ctx.arc(dotX, dotY, 8, 0, Math.PI * 2);
      ctx.fillStyle = "#10b981";
      ctx.fill();
      ctx.strokeStyle = "#ffffff";
      ctx.lineWidth = 2;
      ctx.stroke();

      // Glow effect
      ctx.beginPath();
      ctx.arc(dotX, dotY, 12, 0, Math.PI * 2);
      ctx.fillStyle = "rgba(16, 185, 129, 0.3)";
      ctx.fill();
    }
  }, [bondingCurveProgress]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]:
        name === "decimals" || name === "feeBps" ? parseInt(value) || 0 : value,
    }));

    // If user enters an Image URL manually, update the preview
    if (name === "imageUrl" && value && !file) {
      setPreviewImage(value);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleFile = (selectedFile: File) => {
    if (
      selectedFile.type.startsWith("image/") ||
      selectedFile.type.startsWith("video/")
    ) {
      setFile(selectedFile);
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        setPreviewImage(result);
      };
      reader.readAsDataURL(selectedFile);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus(null);
    setPublishDigest(null);
    setBondingDigest(null);

    try {
      if (!isConnected || !currentAccount) {
        throw new Error("Connect your wallet first");
      }

      if (!form.name.trim()) {
        throw new Error("Token name is required");
      }

      if (!form.symbol.trim()) {
        throw new Error("Token symbol is required");
      }

      if (form.symbol.trim().length > 10) {
        throw new Error("Token symbol must be 10 characters or less");
      }

      if (!file && !form.imageUrl.trim()) {
        throw new Error("Please select an image for your token");
      }

      setStatus("Uploading image...");

      // If a file is selected, upload to backend to get Arweave URL
      let imageUrl = form.imageUrl.trim();
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        const upRes = await fetch(`${BACKEND_URL}/uploads/image`, {
          method: "POST",
          body: fd,
        });
        if (!upRes.ok) throw new Error(await upRes.text());
        const up = await upRes.json();
        imageUrl = (up.gatewayUrl || up.url || "").trim();
        if (imageUrl) {
          await waitForUrl(imageUrl);
        }
        setForm((prev) => ({ ...prev, imageUrl }));
      }

      setStatus("Preparing contract...");

      // Compute raw on-chain max supply = human supply * 10^decimals
      if (!/^\d+$/.test(String(form.maxSupply || ""))) {
        throw new Error("Max Supply must be a whole number");
      }
      const scale = BigInt(10) ** BigInt(form.decimals || 0);
      const human = BigInt(form.maxSupply);
      const rawMaxSupply = (human * scale).toString();

      const prepRes = await fetch(`${BACKEND_URL}/tokens/prepare`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name,
          symbol: form.symbol,
          description: form.description,
          imageUrl,
          decimals: form.decimals,
          maxSupply: rawMaxSupply,
        }),
      });
      if (!prepRes.ok) throw new Error(await prepRes.text());
      const { modules, dependencies } = await prepRes.json();

      setStatus("Publishing contract...");

      const publishTxb = new TransactionBlock();
      const [upgradeCap] = publishTxb.publish({ modules, dependencies });
      publishTxb.transferObjects(
        [upgradeCap],
        publishTxb.pure(currentAccount.address)
      );

      const publishRes = await signAndExecuteTransactionBlock({
        transactionBlock: publishTxb,
        options: { showEffects: true, showObjectChanges: true, showEvents: true },
      });
      setPublishDigest(publishRes.digest);

      // Enhanced package ID extraction with multiple fallbacks
      console.log("Publish response:", publishRes);
      console.log("Object changes:", publishRes.objectChanges);
      console.log("Effects:", publishRes.effects);

      let packageId: string | undefined;

      // Method 1: Standard extraction from objectChanges
      const objectChanges = publishRes.objectChanges || [];
      const packageChange = objectChanges.find(
        (c: any) => c.type === "published"
      ) as any;

      if (packageChange) {
        console.log("Found package change:", packageChange);
        packageId = packageChange.packageId;
      }

      // Method 2: Check effects.created for package objects
      if (!packageId && publishRes.effects?.created) {
        console.log("Trying fallback: checking effects.created");
        const createdObjects = publishRes.effects.created as any[];
        // Package IDs are 66 characters (0x + 64 hex chars)
        for (const obj of createdObjects) {
          if (obj.reference?.objectId && obj.reference.objectId.length === 66) {
            // Check if this is likely a package (not a regular object)
            const id = obj.reference.objectId;
            if (!objectChanges.some((c: any) => c.objectId === id)) {
              packageId = id;
              console.log("Found potential package ID in effects.created:", packageId);
              break;
            }
          }
        }
      }

      // Method 3: Extract from digest if other methods fail
      if (!packageId && publishRes.digest) {
        console.log("Trying to get transaction details by digest:", publishRes.digest);
        try {
          // Import SuiClient if needed
          const { SuiClient, getFullnodeUrl } = await import("@mysten/sui.js/client");
          const client = new SuiClient({ url: getFullnodeUrl("testnet") });
          const txDetails = await client.getTransactionBlock({
            digest: publishRes.digest,
            options: { showObjectChanges: true, showEffects: true }
          });

          const pkgChange = txDetails.objectChanges?.find((c: any) => c.type === "published") as any;
          if (pkgChange?.packageId) {
            packageId = pkgChange.packageId;
            console.log("Found package ID from transaction details:", packageId);
          }
        } catch (err) {
          console.error("Failed to fetch transaction details:", err);
        }
      }

      // Method 4: Look through all object changes for package pattern
      if (!packageId) {
        console.log("Searching all object changes for package pattern");
        for (const change of objectChanges) {
          console.log("Checking change:", change);
          // Cast to any to check various possible field names
          const anyChange = change as any;
          if (anyChange.packageId || anyChange.package || anyChange.packageID) {
            packageId = anyChange.packageId || anyChange.package || anyChange.packageID;
            console.log("Found package ID in change object:", packageId);
            break;
          }
        }
      }

      if (!packageId) {
        console.error("Failed to extract package ID. Full response:", {
          digest: publishRes.digest,
          effects: publishRes.effects,
          objectChanges: publishRes.objectChanges,
          keys: Object.keys(publishRes)
        });

        // Show a more helpful error message
        alert("Package deployment succeeded but we couldn't extract the package ID. Please check the console and try viewing the transaction on the explorer: " + publishRes.digest);
        throw new Error("Package ID extraction failed. Transaction: " + publishRes.digest);
      }

      console.log("Successfully extracted package ID:", packageId);

      const coinType = `${packageId}::meme_token::MEME_TOKEN`;
      console.log("Looking for TreasuryCap with coinType:", coinType);

      // Enhanced TreasuryCap detection with transaction query
      let treasuryCapId: string | undefined;

      // Log all created objects for debugging
      console.log("All objectChanges:", publishRes.objectChanges);
      console.log("Looking for TreasuryCap...");

      // Method 1: Look for TreasuryCap in objectChanges
      const treasuryCap = (publishRes.objectChanges || []).find(
        (c: any) => {
          if (c.type !== "created") return false;
          const objType = c.objectType;
          if (typeof objType !== "string") return false;

          console.log("Checking object type:", objType);

          // Check if it's a TreasuryCap - be more flexible with the check
          if (objType.includes("TreasuryCap")) {
            console.log("Found potential TreasuryCap:", c);
            return true;
          }

          return false;
        }
      );

      if (treasuryCap) {
        treasuryCapId = (treasuryCap as any).objectId;
        console.log("TreasuryCap found - ID:", treasuryCapId);
      }

      // Method 2: Query transaction details for more complete data
      if (!treasuryCapId && publishRes.digest) {
        console.log("Fetching full transaction details for digest:", publishRes.digest);
        try {
          const { SuiClient, getFullnodeUrl } = await import("@mysten/sui.js/client");
          const client = new SuiClient({ url: getFullnodeUrl("testnet") });

          const txDetails = await client.getTransactionBlock({
            digest: publishRes.digest,
            options: {
              showObjectChanges: true,
              showEffects: true,
              showEvents: true,
              showInput: true
            }
          });

          console.log("Full transaction details:", txDetails);

          // Look for TreasuryCap in the detailed response
          const treasuryCapFromTx = txDetails.objectChanges?.find(
            (c: any) => {
              const objType = c.objectType;
              return c.type === "created" &&
                typeof objType === "string" &&
                objType.includes("TreasuryCap");
            }
          );

          if (treasuryCapFromTx) {
            treasuryCapId = (treasuryCapFromTx as any).objectId;
            console.log("Found TreasuryCap from transaction details:", treasuryCapFromTx);
            console.log("TreasuryCap ID:", treasuryCapId);
          }

          // Also save all created objects for debugging
          const createdObjects = txDetails.objectChanges?.filter((c: any) => c.type === "created") || [];
          console.log("All created objects from transaction:", createdObjects);

          // Save to localStorage for debugging
          localStorage.setItem('lastPublishTx', JSON.stringify({
            digest: publishRes.digest,
            packageId,
            objectChanges: txDetails.objectChanges,
            createdObjects
          }));
          console.log("Transaction data saved to localStorage as 'lastPublishTx'");
        } catch (err) {
          console.error("Failed to fetch transaction details:", err);
        }
      }

      // Method 3: Look through ALL created objects
      if (!treasuryCapId) {
        console.log("Final fallback: Checking all created objects");
        const allCreated = (publishRes.objectChanges || []).filter(
          (c: any) => c.type === "created"
        );

        console.log("All created objects:", allCreated);

        // Find the first object that's not the package or UpgradeCap
        for (const obj of allCreated) {
          const objType = (obj as any).objectType;
          if (typeof objType === "string") {
            // Skip UpgradeCap and Package objects
            if (!objType.includes("UpgradeCap") &&
              !objType.includes("package") &&
              (obj as any).objectId) {
              console.log("Potential TreasuryCap candidate:", obj);

              // If it contains "coin" or "TreasuryCap", it's likely our target
              if (objType.includes("coin") || objType.includes("TreasuryCap")) {
                treasuryCapId = (obj as any).objectId;
                console.log("Using this as TreasuryCap:", obj);
                break;
              }
            }
          }
        }
      }

      if (!treasuryCapId) {
        // Provide detailed error information
        const errorInfo = {
          digest: publishRes.digest,
          packageId,
          objectChanges: publishRes.objectChanges,
          effects: publishRes.effects
        };

        console.error("TreasuryCap not found. Debug info:", errorInfo);

        // Save error info to localStorage
        localStorage.setItem('treasuryCapError', JSON.stringify(errorInfo));

        alert(`TreasuryCap not found. Transaction digest: ${publishRes.digest}\n\nCheck console and localStorage for debug info.\n\nYou can view the transaction at:\nhttps://testnet.suivision.xyz/txblock/${publishRes.digest}`);

        throw new Error("TreasuryCap not found. Debug info saved to localStorage as 'treasuryCapError'");
      }

      console.log("✅ TreasuryCap successfully found:", treasuryCapId);

      setStatus("Creating bonding curve...");

      const bondingTxb = new TransactionBlock();
      bondingTxb.moveCall({
        target: `${packageId}::bonding_curve::create_bonding_curve`,
        arguments: [
          bondingTxb.object(treasuryCapId),
          bondingTxb.pure(form.feeBps),
        ],
      });

      const bondingRes = await signAndExecuteTransactionBlock({
        transactionBlock: bondingTxb,
        options: { showEffects: true, showObjectChanges: true },
      });
      setBondingDigest(bondingRes.digest);

      console.log("=== BONDING CURVE TRANSACTION DEBUG ===");
      console.log("Full bonding response:", JSON.stringify(bondingRes, null, 2));
      console.log("Object changes:", bondingRes.objectChanges);
      console.log("Effects:", bondingRes.effects);

      // Enhanced BondingCurve detection - be more flexible with objectType matching
      let bondingCurveId: string | undefined;

      console.log("All object changes for bonding curve transaction:");
      (bondingRes.objectChanges || []).forEach((c: any, index: number) => {
        console.log(`Change ${index}:`, {
          type: c.type,
          objectType: c.objectType,
          objectId: c.objectId,
          hasBondingCurve: c.objectType?.includes?.('BondingCurve') || c.objectType?.includes?.('bonding_curve')
        });
      });

      const bondingCurve = (bondingRes.objectChanges || []).find(
        (c: any) => {
          if (c.type !== "created") return false;
          const objType = c.objectType;
          if (typeof objType !== "string") return false;

          console.log("Checking for BondingCurve:", objType);

          // Check if it's a BondingCurve - be more flexible with the check
          if (objType.includes("bonding_curve::BondingCurve") ||
            objType.includes("::bonding_curve::") ||
            objType.includes("BondingCurve") ||
            objType.toLowerCase().includes("bonding") ||
            objType.toLowerCase().includes("curve")) {
            console.log("Found BondingCurve:", c);
            return true;
          }

          return false;
        }
      );

      if (bondingCurve) {
        bondingCurveId = (bondingCurve as any).objectId;
        console.log("BondingCurve ID:", bondingCurveId);
      }

      // Fallback: try to find any created object that might be the bonding curve
      if (!bondingCurveId) {
        console.log("Primary detection failed, trying fallback methods...");

        // Method 1: Look for any object with "curve" in the type
        const curveFallback = (bondingRes.objectChanges || []).find(
          (c: any) => c.type === "created" &&
            c.objectType &&
            typeof c.objectType === "string" &&
            (c.objectType.toLowerCase().includes("curve") || c.objectType.toLowerCase().includes("bonding"))
        );

        if (curveFallback) {
          bondingCurveId = (curveFallback as any).objectId;
          console.log("Found bonding curve via fallback method 1:", bondingCurveId);
        }

        // Method 2: If we have effects.created, check those too
        if (!bondingCurveId && bondingRes.effects?.created) {
          console.log("Checking effects.created for bonding curve...");
          const { SuiClient, getFullnodeUrl } = await import("@mysten/sui.js/client");
          const client = new SuiClient({ url: getFullnodeUrl("testnet") });
          const createdObjects = bondingRes.effects.created as any[];
          for (const obj of createdObjects) {
            if (obj.reference?.objectId) {
              const objDetails = await client.getObject({
                id: obj.reference.objectId,
                options: { showContent: true }
              });
              console.log("Created object details:", objDetails);

              if (objDetails.data?.content &&
                typeof objDetails.data.content === "object" &&
                "type" in objDetails.data.content &&
                typeof (objDetails.data.content as any).type === "string" &&
                (objDetails.data.content as any).type.toLowerCase().includes("bonding")) {
                bondingCurveId = obj.reference.objectId;
                console.log("Found bonding curve in effects.created:", bondingCurveId);
                break;
              }
            }
          }
        }

        // Method 4: Final fallback - find any created object that isn't treasury cap or package
        if (!bondingCurveId) {
          console.log("Trying method 4: final fallback for any created object");
          const allCreated = (bondingRes.objectChanges || []).filter((c: any) => c.type === "created");

          console.log("All created objects:", allCreated);

          // Find objects that aren't treasury cap or package (by checking objectType or objectId length)
          for (const obj of allCreated) {
            const objType = (obj as any).objectType;
            const objId = (obj as any).objectId;

            console.log("Checking object:", { objType, objId });

            // Skip known objects
            if (objType?.includes?.("TreasuryCap") ||
              objType?.includes?.("package") ||
              objId === treasuryCapId ||
              objId === packageId) {
              console.log("Skipping known object:", objType, objId);
              continue;
            }

            // If it's a 66-character object ID and not the treasury cap or package, it's likely the bonding curve
            if (objId && objId.length === 66) {
              bondingCurveId = objId;
              console.log("Found bonding curve via method 4 (final fallback):", bondingCurveId);
              break;
            }
          }
        }

        // Method 5: If all else fails, try to use the transaction digest to query the blockchain
        if (!bondingCurveId && bondingRes.digest) {
          console.log("Trying method 5: query transaction details for bonding curve");
          try {
            const { SuiClient, getFullnodeUrl } = await import("@mysten/sui.js/client");
            const client = new SuiClient({ url: getFullnodeUrl("testnet") });

            const txDetails = await client.getTransactionBlock({
              digest: bondingRes.digest,
              options: { showObjectChanges: true, showEffects: true }
            });

            console.log("Transaction details from blockchain:", txDetails);

            // Look through all created objects in the detailed response
            const createdObjects = txDetails.objectChanges?.filter((c: any) => c.type === "created") || [];

            for (const obj of createdObjects) {
              const objType = (obj as any).objectType;
              const objId = (obj as any).objectId;

              console.log("Checking detailed object:", { objType, objId });

              // Skip known objects
              if (objType?.includes?.("TreasuryCap") ||
                objType?.includes?.("package") ||
                objId === treasuryCapId ||
                objId === packageId) {
                console.log("Skipping known object in details:", objType, objId);
                continue;
              }

              // If it's a 66-character object ID and not the treasury cap or package, it's likely the bonding curve
              if (objId && objId.length === 66) {
                bondingCurveId = objId;
                console.log("Found bonding curve via method 5 (transaction details):", bondingCurveId);
                break;
              }
            }
          } catch (error) {
            console.error("Failed to query transaction details:", error);
          }
        }
      }

      if (!bondingCurveId) {
        console.error("BondingCurve not found. All object changes:", bondingRes.objectChanges);
        console.error("Transaction digest:", bondingRes.digest);
        console.error("Full bonding response:", JSON.stringify(bondingRes, null, 2));

        // Save error details to localStorage for debugging
        localStorage.setItem('bondingCurveError', JSON.stringify({
          digest: bondingRes.digest,
          objectChanges: bondingRes.objectChanges,
          effects: bondingRes.effects,
          timestamp: new Date().toISOString()
        }));

        // Final fallback: Ask user to manually identify the bonding curve ID
        const userProvidedId = prompt(
          `Bonding curve detection failed. Please check the transaction on the explorer and enter the bonding curve object ID manually.\n\n` +
          `Transaction: https://suiscan.xyz/testnet/tx/${bondingRes.digest}\n\n` +
          `Look for an object that was created in this transaction that is NOT the TreasuryCap or Package.\n` +
          `Enter the 66-character object ID:`
        );

        if (userProvidedId && userProvidedId.length === 66 && userProvidedId.startsWith('0x')) {
          bondingCurveId = userProvidedId;
          console.log("User provided bonding curve ID:", bondingCurveId);
        } else {
          throw new Error("BondingCurve id not found. Check console for details and localStorage 'bondingCurveError' for debug info.");
        }
      }

      console.log("✅ BondingCurve successfully found:", bondingCurveId);

      // Enhanced CoinMetadata detection with better search strategy
      let metadataId = "";
      console.log("🔍 Starting CoinMetadata extraction...");

      // Method 1: Look in publish transaction first
      const coinMetadata = (publishRes.objectChanges || []).find(
        (c: any) => {
          if (c.type !== "created") return false;
          const objType = c.objectType;
          if (typeof objType !== "string") return false;

          console.log("Checking object type:", objType);

          // Look for CoinMetadata pattern - be more flexible with the check
          if (objType.includes("::coin::CoinMetadata") ||
            objType.includes("coin::CoinMetadata") ||
            objType.toLowerCase().includes("coinmetadata")) {
            console.log("✅ Found CoinMetadata:", objType);
            return true;
          }

          return false;
        }
      );

      if (coinMetadata) {
        metadataId = (coinMetadata as any).objectId;
        console.log("✅ CoinMetadata ID extracted:", metadataId);
      } else {
        // Method 2: Try to find in bonding curve transaction
        console.log("❌ CoinMetadata not found in publish transaction, checking bonding curve transaction...");
        const bondingMetadata = (bondingRes.objectChanges || []).find(
          (c: any) => {
            if (c.type !== "created") return false;
            const objType = c.objectType;
            if (typeof objType !== "string") return false;

            console.log("Checking bonding object type:", objType);

            if (objType.includes("::coin::CoinMetadata") ||
              objType.includes("coin::CoinMetadata") ||
              objType.toLowerCase().includes("coinmetadata")) {
              console.log("✅ Found CoinMetadata in bonding transaction:", objType);
              return true;
            }

            return false;
          }
        );

        if (bondingMetadata) {
          metadataId = (bondingMetadata as any).objectId;
          console.log("✅ CoinMetadata ID extracted from bonding transaction:", metadataId);
        } else {
          // Method 3: Query blockchain for MEME_TOKEN metadata objects with delay and retry
          console.log("❌ CoinMetadata not found in transactions, querying blockchain...");
          try {
            const { SuiClient, getFullnodeUrl } = await import("@mysten/sui.js/client");
            const client = new SuiClient({ url: getFullnodeUrl("testnet") });

            // Wait a bit first - metadata creation might be asynchronous
            console.log("⏳ Waiting for metadata to be discoverable...");
            await new Promise(resolve => setTimeout(resolve, 3000));

            // Try to find any CoinMetadata objects owned by the DEPLOYER (not the package)
            // The metadata is transferred to the sender/deployer, not the package
            const deployerObjects = await client.getOwnedObjects({
              owner: currentAccount.address, // Use the sender/deployer address, not packageId
              options: { showType: true, showContent: false }
            });

            console.log(`Found ${deployerObjects.data?.length || 0} objects owned by deployer`);

            for (const obj of deployerObjects.data || []) {
              console.log(`Object: ${obj.data?.objectId} - Type: ${obj.data?.type}`);
              if (obj.data?.type && obj.data.type.includes('CoinMetadata')) {
                metadataId = obj.data.objectId;
                console.log("✅ Found CoinMetadata owned by deployer:", metadataId);
                break;
              }
            }

            // Method 4: If still not found, try deterministic ID construction with delay
            if (!metadataId) {
              console.log("❌ Still no metadata found, trying deterministic ID construction...");
              const expectedMetadataId = `${packageId}::meme_token::CoinMetadata`;

              // Wait a bit more and retry
              await new Promise(resolve => setTimeout(resolve, 2000));

              try {
                const metadataObject = await client.getObject({
                  id: expectedMetadataId,
                  options: { showContent: true }
                });

                if (metadataObject.data && metadataObject.data.content) {
                  metadataId = expectedMetadataId;
                  console.log("✅ Found CoinMetadata via deterministic ID:", metadataId);
                }
              } catch (error) {
                console.warn("Deterministic metadata ID not found:", error);
              }
            }

            // Method 5: Try alternative metadata ID patterns with additional wait
            if (!metadataId) {
              console.log("❌ Trying alternative metadata ID patterns...");
              const alternativeIds = [
                `${packageId}::coin::CoinMetadata`,
                `${coinType}::coin::CoinMetadata`,
                `0x2::coin::CoinMetadata`, // Fallback to SUI metadata for testing
              ];

              for (const altId of alternativeIds) {
                try {
                  const testObject = await client.getObject({
                    id: altId,
                    options: { showContent: true }
                  });

                  if (testObject.data && testObject.data.content) {
                    metadataId = altId;
                    console.log("✅ Found alternative metadata ID:", metadataId);
                    break;
                  }
                } catch (error) {
                  console.warn(`Alternative ID ${altId} not found:`, error);
                }
              }
            }

            // Method 6: Final attempt - try to query by package ID pattern
            if (!metadataId && packageId) {
              console.log("❌ Trying package ID pattern search...");
              try {
                // Look for any objects that start with the package ID
                const allObjects = await client.getOwnedObjects({
                  owner: currentAccount.address,
                  options: { showType: true, showContent: false }
                });

                for (const obj of allObjects.data || []) {
                  if (obj.data?.objectId && obj.data.objectId.startsWith(packageId.slice(0, 10))) {
                    // Try to get the object details to check if it's metadata
                    try {
                      const objDetails = await client.getObject({
                        id: obj.data.objectId,
                        options: { showContent: true }
                      });

                      if (objDetails.data?.content &&
                        typeof objDetails.data.content === "object" &&
                        "type" in objDetails.data.content &&
                        typeof (objDetails.data.content as any).type === "string" &&
                        (objDetails.data.content as any).type.includes("CoinMetadata")) {
                        metadataId = obj.data.objectId;
                        console.log("✅ Found metadata by package ID pattern:", metadataId);
                        break;
                      }
                    } catch (error) {
                      console.warn(`Failed to check object ${obj.data.objectId}:`, error);
                    }
                  }
                }
              } catch (error) {
                console.error("Package ID pattern search failed:", error);
              }
            }
          } catch (error) {
            console.error("Failed to query blockchain for metadata:", error);
          }
        }
      }

      if (!metadataId) {
        console.log("❌ CoinMetadata not found - this may cause issues with buy/sell");
        console.log("📋 Available objects in publish transaction:");
        (publishRes.objectChanges || []).forEach((obj: any, index: number) => {
          console.log(`${index}: ${obj.type} - ${obj.objectType} - ${obj.objectId}`);
        });
        console.log("📋 Available objects in bonding transaction:");
        (bondingRes.objectChanges || []).forEach((obj: any, index: number) => {
          console.log(`${index}: ${obj.type} - ${obj.objectType} - ${obj.objectId}`);
        });
      } else {
        console.log("✅ Final metadataId result:", metadataId);
      }

      setStatus("Registering token...");

      // Validate that we have all required IDs before proceeding (metadata optional with fallback)
      const requiredIds = {
        packageId,
        treasuryCapId,
        bondingCurveId,
      };

      const missing = Object.entries(requiredIds)
        .filter(([_, value]) => !value)
        .map(([key]) => key);

      if (missing.length > 0) {
        console.error("Missing required IDs:", missing);
        throw new Error(`Missing required IDs: ${missing.join(", ")}. Cannot proceed with token registration.`);
      }

      if (!metadataId) {
        console.warn(
          "Proceeding without metadataId. Trading UI will attempt to resolve metadata on-demand."
        );
      }

      console.log("All required IDs validated (metadata optional):", {
        packageId,
        treasuryCapId,
        bondingCurveId,
        metadataId,
        coinType
      });

      // Get user profile for creator information
      const userAddress = currentAccount.address;
      const savedProfile = localStorage.getItem(`profile_${userAddress}`);
      let creatorUsername = `${userAddress.slice(0, 4)}...${userAddress.slice(-4)}`;

      if (savedProfile) {
        try {
          const profile = JSON.parse(savedProfile);
          creatorUsername = profile.username || creatorUsername;
        } catch (error) {
          console.error('Error parsing user profile:', error);
        }
      }

      const registerPayload: Record<string, unknown> = {
        name: form.name,
        symbol: form.symbol,
        description: form.description,
        imageUrl: imageUrl,
        decimals: form.decimals,
        contractPackageId: packageId,
        bondingCurveId,
        coinType,
        creatorWallet: userAddress,
        creatorUsername: creatorUsername,
      };

      if (typeof metadataId === "string" && metadataId.trim().length > 0) {
        registerPayload.metadataId = metadataId;
      }

      const registerRes = await fetch(`${BACKEND_URL}/tokens/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(registerPayload),
      });
      if (!registerRes.ok) throw new Error(await registerRes.text());

      setStatus("Token deployed and registered!");
      onCreated();

      // Additional retry mechanism specifically for token creation
      console.log("Token creation completed, triggering additional token load...");
      setTimeout(() => {
        console.log("Triggering delayed token reload...");
        onCreated();
      }, 1000);

      // Reset form
      setForm({
        name: "",
        symbol: "",
        description: "",
        imageUrl: "",
        decimals: 9,
        maxSupply: "100000000000000",
        feeBps: 300,
      });
      setFile(null);
      setPreviewImage(null);
      setBondingCurveProgress(0);
    } catch (error: any) {
      console.error("Error creating token:", error);
      setStatus("Error: " + (error.message || String(error)));
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid =
    form.name.trim() &&
    form.symbol.trim() &&
    (file || form.imageUrl.trim()) &&
    isConnected;

  return (
    <div className="enhanced-create-container">
      <div className="create-content">
        <div className="create-form-section">
          <div className="form-header">
            <h1 className="form-title">Create New Meme Token</h1>
            <p className="form-subtitle">
              You will sign Publish and Bonding Curve Init transactions.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="create-form">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="name">Name</label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  value={form.name}
                  onChange={handleInputChange}
                  placeholder="Doge Coin"
                  className="form-input"
                  disabled={isSubmitting}
                />
              </div>
              <div className="form-group">
                <label htmlFor="symbol">Ticker</label>
                <input
                  id="symbol"
                  name="symbol"
                  type="text"
                  value={form.symbol}
                  onChange={handleInputChange}
                  placeholder="DOGE"
                  className="form-input"
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="imageUrl">Image URL (Optional)</label>
              <input
                id="imageUrl"
                name="imageUrl"
                type="text"
                value={form.imageUrl}
                onChange={handleInputChange}
                placeholder="https://example.com/image.png"
                className="form-input"
                disabled={isSubmitting}
              />
              <p className="form-help">Enter a URL or upload a file below</p>
            </div>

            <div className="image-upload-section">
              <label>Image</label>
              <div
                className={`image-upload-area ${dragActive ? "drag-active" : ""
                  } ${previewImage ? "has-image" : ""}`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                {previewImage ? (
                  <div className="image-preview">
                    <img src={previewImage} alt="Token preview" />
                    <div className="image-overlay">
                      <span>Click to change</span>
                    </div>
                  </div>
                ) : (
                  <div className="upload-placeholder">
                    <div className="upload-icon">📷</div>
                    <div className="upload-text">
                      <p>Select video or image to upload</p>
                      <p className="upload-subtext">or drag and drop it here</p>
                    </div>
                    <button type="button" className="select-file-btn">
                      Select file
                    </button>
                  </div>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,video/*"
                onChange={handleFileSelect}
                className="file-input-hidden"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="maxSupply">Max Supply (whole tokens)</label>
                <input
                  id="maxSupply"
                  name="maxSupply"
                  type="text"
                  value={form.maxSupply}
                  onChange={handleInputChange}
                  placeholder="1000000000000"
                  className="form-input"
                  disabled={isSubmitting}
                />
              </div>
              <div className="form-group">
                <label htmlFor="decimals">Decimals</label>
                <input
                  id="decimals"
                  name="decimals"
                  type="number"
                  value={form.decimals}
                  onChange={handleInputChange}
                  placeholder="9"
                  className="form-input"
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group full-width">
                <label htmlFor="feeBps">Fee (bps)</label>
                <input
                  id="feeBps"
                  name="feeBps"
                  type="number"
                  value={form.feeBps}
                  onChange={handleInputChange}
                  placeholder="300"
                  className="form-input"
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                name="description"
                value={form.description}
                onChange={handleInputChange}
                placeholder="Much wow, very meme"
                className="form-textarea"
                rows={4}
                disabled={isSubmitting}
              />
            </div>

            <button
              type="submit"
              className="deploy-button"
              disabled={!isFormValid || isSubmitting}
            >
              <span className="deploy-button-icon">🚀</span>
              <span>
                {isSubmitting
                  ? status || "Creating Token..."
                  : "Deploy with Wallet"}
              </span>
            </button>

            {status && (
              <div
                className={`status-message ${status.includes("successfully") ||
                  status.includes("registered")
                  ? "success"
                  : status.includes("Error") || status.includes("Failed")
                    ? "error"
                    : "info"
                  }`}
              >
                {status}
              </div>
            )}

            {publishDigest && (
              <div className="transaction-link">
                <a
                  href={`https://suiscan.xyz/testnet/tx/${publishDigest}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="tx-link"
                >
                  View Publish Transaction: {publishDigest.substring(0, 10)}...
                </a>
              </div>
            )}

            {bondingDigest && (
              <div className="transaction-link">
                <a
                  href={`https://suiscan.xyz/testnet/tx/${bondingDigest}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="tx-link"
                >
                  View Bonding Curve Transaction:{" "}
                  {bondingDigest.substring(0, 10)}...
                </a>
              </div>
            )}
          </form>
        </div>

        <div className="preview-section">
          <div className="preview-sticky-wrapper">
            <h2 className="preview-heading">Live Preview</h2>
            <EnhancedTokenPreview
              name={form.name}
              symbol={form.symbol}
              description={form.description}
              imageUrl={form.imageUrl}
              maxSupply={form.maxSupply}
              decimals={form.decimals}
              feeBps={form.feeBps}
              file={file}
            />
          </div>
        </div>
      </div>

      <div className="create-footer">
        <div className="footer-stats">
          <div className="footer-stat">
            <span className="footer-stat-label">ACTIVE TOKENS</span>
            <span className="footer-stat-value">1,247</span>
          </div>
          <div className="footer-stat">
            <span className="footer-stat-label">24H VOLUME</span>
            <span className="footer-stat-value">$2.4M</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnhancedCreateTokenForm;